import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { dash, VIRT_MIN, VIRT_OVER, type Row } from '../components/DataTable';
import { useFitHeight } from '../lib/fitHeight';

/**
 * 접수 **고르는 목록** — 좌우 2단의 왼쪽 칸.
 *
 * ⚠️ **표를 쓰지 않는다.** 표는 칸 너비가 가장 긴 값에 끌려가는데 거래처명이
 * `한국외국어대학교글로벌 스포츠산업부` 처럼 길다. 좁은 칸(약 22rem)에서 표로 만들면
 * `.td` 의 `whitespace-nowrap` 때문에 가로로 삐져나가고 **뒤쪽 칸(노선수·상태)이 잘려 안 보인다**
 * (2026-07-30 실측 사고). 줄바꿈을 허용하는 목록이면 그 문제가 **구조적으로** 사라진다.
 *
 * 한 항목 = 두 줄. 위는 거래처(구분 태그 + 이름, 줄바꿈 허용), 아래는 기간·노선수·상태.
 * 한국어는 `break-keep` 으로 **어절 중간을 쪼개지 않게** 한다(기본값은 쪼갠다).
 */
/** `2026-08-01` → `08-01`. 좁은 칸에서 연도는 자리만 먹는다(오른쪽 요약 카드에 온전히 있다). */
const md = (v: unknown) => {
  const s = dash(v);
  return s === '—' ? s : s.slice(5) || s;
};

function Dot({ status }: { status: string }) {
  const tone =
    status === '완료' ? 'bg-emerald-500' : status === '취소' ? 'bg-ink-faint' : 'bg-amber-500';
  return <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} />;
}

export default function RecvList({
  rows,
  loading,
  selectedId,
  onPick,
}: {
  rows: Row[];
  loading: boolean;
  selectedId: number | null;
  onPick: (recvId: number) => void;
}) {
  /**
   * 목록을 **뷰포트 높이 안에 묶고 목록 안에서만** 스크롤한다(2026-09-17 사용자 요청).
   *
   * 왜: 한 쪽이 50건이라 목록이 화면 두세 장 높이다. 페이지째 내려가면 오른쪽 요약·노선 카드가
   * 같이 위로 사라져, 아래쪽 접수를 고르고 나면 **결과를 보려고 다시 올라가야** 했다.
   * 오른쪽 노선 표(`RecvRoutes` 의 `max-h`)와 같은 방식 — 칸마다 제 스크롤바를 갖는다.
   *
   * 높이는 `DataTable` 과 같은 식으로 **박스가 놓인 자리**에서 잰다(위에 복사 배너가 뜨면 내려간다).
   * 아래 `Pager` 줄은 `useFitHeight` 가 실제 높이를 재서 뺀다(#255) — 안 빼면 쪽 넘김 버튼이 화면 밖으로 밀린다.
   */
  const boxRef = useRef<HTMLDivElement>(null);
  const maxHeight = useFitHeight(boxRef, [rows.length, loading], { min: '20rem' });

  // 쪽을 넘기거나 다시 검색하면 목록 맨 위부터 — 안 그러면 새 목록의 중간에서 시작한다.
  // ⚠️ `rows` 가 아니라 **첫 줄 id** 에 건다. 노선을 저장하면 목록을 다시 읽는데(같은 쪽),
  //    그때마다 맨 위로 튀면 방금 고른 아래쪽 접수를 다시 찾아 내려가야 한다.
  const firstId = rows[0]?.recv_id;
  useEffect(() => {
    boxRef.current?.scrollTo({ top: 0 });
  }, [firstId]);

  // 같은 접수 번호가 두 번 오면 한 번만 그린다 — 키가 겹치면 React 가 옛 줄을 지우지 못해 남긴다(#278).
  // Set 으로 한 번에 — `findIndex` 는 줄 수의 제곱이라 「전체」(5,000줄)에서 렌더마다 수백 ms 였다(issues 244).
  const list = useMemo(() => {
    const seen = new Set<number>();
    return rows.filter((r) => { const id = Number(r.recv_id); if (seen.has(id)) return false; seen.add(id); return true; });
  }, [rows]);

  // ── 보이는 줄만 그리기 — `DataTable`(#241)과 같은 식. 「전체」 쪽 크기면 5,000줄 × 11요소 = DOM 5.5만이라
  //    접수↔노선 전환마다 0.4~0.9초 멈췄다(10-22 logPerf · ilyang). 줄 높이는 이름 줄바꿈으로 달라 그린 줄 평균을 쓴다.
  const virt = list.length > VIRT_MIN;
  const [scroll, setScroll] = useState({ top: 0, h: 900 });
  const [rowH, setRowH] = useState(52);
  const scrollRaf = useRef(0);
  // 🔴 줄 높이는 스크롤 한 번(또는 새 자료)에 한 번만 잰다 — 매 렌더마다 재면 높이↔범위가 서로를 바꾸며 돈다(DataTable #295).
  const measureOk = useRef(true);
  const measuredRows = useRef<Row[] | null>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !virt) return;
    const read = () => { scrollRaf.current = 0; measureOk.current = true; setScroll({ top: el.scrollTop, h: el.clientHeight }); };
    const onScroll = () => { if (!scrollRaf.current) scrollRaf.current = requestAnimationFrame(read); };
    read();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current);
      scrollRaf.current = 0;
    };
  }, [virt, maxHeight]);
  useLayoutEffect(() => {
    if (!virt) return;
    if (measuredRows.current !== rows) { measuredRows.current = rows; measureOk.current = true; }
    if (!measureOk.current) return;
    const items = Array.from(boxRef.current?.querySelectorAll<HTMLElement>(':scope > button') ?? []);
    if (!items.length) return;
    measureOk.current = false;
    const avg = items.reduce((n, b) => n + b.offsetHeight, 0) / items.length;
    if (avg > 0 && Math.abs(avg - rowH) > 0.5) setRowH(avg);
  });
  const vStart = virt ? Math.min(Math.max(0, Math.floor(scroll.top / rowH) - VIRT_OVER), Math.max(0, list.length - 1)) : 0;
  const vEnd = virt ? Math.min(list.length, vStart + Math.ceil(scroll.h / rowH) + VIRT_OVER * 2) : list.length;

  if (!loading && rows.length === 0) {
    return <div className="card p-4 text-sm text-ink-faint">조회 결과가 없습니다.</div>;
  }

  return (
    <div ref={boxRef} className="card divide-y divide-line overflow-auto" style={{ maxHeight }}>
      {virt && vStart > 0 && <div aria-hidden style={{ height: vStart * rowH }} />}
      {list.slice(vStart, vEnd).map((r) => {
        const id = Number(r.recv_id);
        const on = id === selectedId;
        const commute = dash(r.recv_type) === '통근';
        const status = dash(r.status);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onPick(id)}
            className={`block w-full px-3 py-2 text-left ${on ? 'bg-brand-50' : 'hover:bg-canvas'}`}
          >
            <div className="flex items-start gap-1.5">
              <span
                className={`mt-0.5 shrink-0 rounded px-1 py-0.5 text-[11px] font-medium ${
                  commute ? 'bg-violet-50 text-violet-700' : 'bg-canvas text-ink-soft'
                }`}
              >
                {dash(r.recv_type)}
              </span>
              {/* 이름은 잘리지 않는다 — 길면 다음 줄로 넘어간다(어절 단위). */}
              <span className="break-keep text-sm font-medium">{dash(r.cust_name)}</span>
              {/* 접수 NO = 레거시 RecvId 와 같은 번호. 담당자끼리 이 번호로 소통한다(검색창에 숫자만 치면 찾는다). */}
              <span className="ml-auto shrink-0 pt-0.5 text-[11px] text-ink-faint">NO {id}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-1 text-xs text-ink-soft">
              <span>
                {md(r.recv_startdt)}~{md(r.recv_enddt)}
              </span>
              <span className="text-ink-faint">·</span>
              <span>노선 {dash(r.dispt_cnt)}</span>
              <span className="ml-auto flex items-center gap-1">
                <Dot status={status} />
                {status}
              </span>
            </div>
          </button>
        );
      })}
      {virt && vEnd < list.length && <div aria-hidden style={{ height: (list.length - vEnd) * rowH }} />}
      {loading && <div className="px-3 py-2 text-xs text-ink-faint">불러오는 중…</div>}
    </div>
  );
}
